// src/contexts/pedidos/WhatsappWebContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchData, postData } from '../../services/Api';
import { enviarMensagemTexto as enviarMensagemTextoService, getUrlApp } from '../../services/pedidos/WhatsService';
import { useAlert } from '../geral/AlertContext';
import { useWhatsappWebSSE } from './WhatsappWebSSE';

const WhatsAppWebContext = createContext();

export const useWhatsAppWeb = () => useContext(WhatsAppWebContext);

const normalizarTelefoneWhatsApp = (telefone = '') => {
  const telefoneNumerico = telefone.toString().replace(/\D/g, '');

  if (!telefoneNumerico) {
    return '';
  }

  if (telefoneNumerico.startsWith('55') && (telefoneNumerico.length === 12 || telefoneNumerico.length === 13)) {
    return telefoneNumerico;
  }

  if (telefoneNumerico.length === 10 || telefoneNumerico.length === 11) {
    return `55${telefoneNumerico}`;
  }

  return telefoneNumerico;
};

const getTelefoneWhatsAppValido = (result) => {
  if (typeof result === 'boolean') {
    return result;
  }

  if (typeof result === 'string') {
    const valorNormalizado = result.trim().toLowerCase();
    return valorNormalizado === 'true' || valorNormalizado === 'ok' || valorNormalizado === 'sucesso';
  }

  if (typeof result === 'number') {
    return result > 0;
  }

  if (!result || typeof result !== 'object') {
    return false;
  }

  const valorEncontrado =
    result.valido ??
    result.isValid ??
    result.valid ??
    result.sucesso ??
    result.success ??
    result.exists ??
    result.existe ??
    result.encontrado;

  if (typeof valorEncontrado === 'boolean') {
    return valorEncontrado;
  }

  return false;
};

export const checkWhatsNumber = async (telefone) => {
  const telefoneNormalizado = normalizarTelefoneWhatsApp(telefone);

  if (!telefoneNormalizado) {
    return {
      valido: false,
      telefone: '',
      response: null,
    };
  }

  const result = await postData('/WhatsAppWeb/CheckWhatsNumber', {
    telefone: telefoneNormalizado,
  });

  return {
    valido: getTelefoneWhatsAppValido(result),
    telefone: telefoneNormalizado,
    response: result,
  };
};

const getContatoId = (contato) => (
  contato?.Id ??
  contato?.contatoId ??
  contato?.ContatoId ??
  contato?.chatBotContatoId ??
  contato?.ChatBotContatoId ??
  contato?.chatBotMensagemId ??
  contato?.ChatBotMensagemId ??
  contato?.whatsAppContatoId ??
  contato?.WhatsAppContatoId ??
  contato?.whatsappContatoId ??
  contato?.WhatsappContatoId ??
  contato?.id ??
  null
);

const getContatoKey = (contato) => String(contato?.contatoIdApi ?? getContatoId(contato) ?? contato?.id ?? '');

export const WhatsAppWebProvider = ({ children }) => {
  const [contatos, setContatos] = useState([]);
  const [contatoSelecionado, setContatoSelecionado] = useState(null);
  const [mensagensContato, setMensagensContato] = useState([]);
  const [ultimaSincronizacao, setUltimaSincronizacao] = useState(null);
  const [loadingContatos, setLoadingContatos] = useState(false);
  const [loadingMensagens, setLoadingMensagens] = useState(false);
  const ultimaSincronizacaoRef = useRef(null);
  const contatoSelecionadoRef = useRef(null);
  const sincronizandoRef = useRef(false);
  const { showAlert } = useAlert();

  const parseDataAlteracao = useCallback((valor) => {
    if (!valor) return null;

    if (valor instanceof Date) {
      return Number.isNaN(valor.getTime()) ? null : valor;
    }

    const texto = String(valor).trim().replace(/^"+|"+$/g, '');
    if (!texto) return null;

    const data = new Date(texto);
    return Number.isNaN(data.getTime()) ? null : data;
  }, []);

  const getContatoTimestamp = useCallback((contato) => {
    const data = parseDataAlteracao(
      contato?.ultimaDataHora ??
      contato?.UltimaDataHora ??
      contato?.dataHora ??
      contato?.DataHora
    );
    return data ? data.getTime() : 0;
  }, [parseDataAlteracao]);

  const normalizarContato = useCallback((contato, fallbackId) => ({
    ...contato,
    id: contato?.id ?? contato?.Id ?? fallbackId ?? `tmp-${getContatoKey(contato) || 'contato'}`,
    nome: contato?.nome ?? contato?.Nome ?? '',
    telefone: contato?.telefone ?? contato?.Telefone ?? '',
    mensagem: contato?.mensagem ?? contato?.Mensagem ?? '',
    ultimaMensagem: contato?.ultimaMensagem ?? contato?.UltimaMensagem ?? contato?.mensagem ?? contato?.Mensagem ?? '',
    dataHora: contato?.dataHora ?? contato?.DataHora ?? null,
    ultimaDataHora: contato?.ultimaDataHora ?? contato?.UltimaDataHora ?? contato?.dataHora ?? contato?.DataHora ?? null,
    contatoIdApi: contato?.contatoIdApi ?? getContatoId(contato),
    hasUnread: !!contato?.hasUnread,
  }), []);

  const ordenarContatos = useCallback((lista) => (
    [...lista].sort((a, b) => {
      const diferencaData = getContatoTimestamp(b) - getContatoTimestamp(a);
      if (diferencaData !== 0) return diferencaData;

      const nomeA = String(a?.nome ?? a?.telefone ?? '').toLowerCase();
      const nomeB = String(b?.nome ?? b?.telefone ?? '').toLowerCase();
      return nomeA.localeCompare(nomeB);
    })
  ), [getContatoTimestamp]);

  const atualizarUltimaSincronizacao = useCallback((data) => {
    if (!data || Number.isNaN(data.getTime())) return;
    ultimaSincronizacaoRef.current = data;
    setUltimaSincronizacao(data.toISOString());
  }, []);

  const extrairContatoDoEvento = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    if (payload.contato && typeof payload.contato === 'object') {
      return payload.contato;
    }

    if (payload.chatContato && typeof payload.chatContato === 'object') {
      return payload.chatContato;
    }

    return payload;
  }, []);

  const mergeContatos = useCallback((contatosAtuais, contatosRecentes) => {
    const contatoSelecionadoAtual = contatoSelecionadoRef.current;
    const contatoSelecionadoKey = contatoSelecionadoAtual ? getContatoKey(contatoSelecionadoAtual) : '';
    const mapa = new Map(
      (contatosAtuais || []).map((contato, index) => {
        const normalizado = normalizarContato(contato, contato?.id ?? `tmp-${index}`);
        return [getContatoKey(normalizado), normalizado];
      })
    );
    const contatosAlterados = [];

    (contatosRecentes || []).forEach((contatoRecente, index) => {
      const fallbackId = contatoRecente?.id ?? `tmp-${getContatoId(contatoRecente) ?? index}`;
      const normalizado = normalizarContato(contatoRecente, fallbackId);
      const contatoKey = getContatoKey(normalizado);
      if (!contatoKey) return;

      const atual = mapa.get(contatoKey);
      const timestampAtual = atual ? getContatoTimestamp(atual) : 0;
      const timestampNovo = getContatoTimestamp(normalizado);
      const teveNovaMensagem = !atual || timestampNovo > timestampAtual;

      if (!teveNovaMensagem) {
        return;
      }

      const merged = {
        ...(atual || {}),
        ...normalizado,
        hasUnread: contatoKey === contatoSelecionadoKey ? false : true,
      };

      mapa.set(contatoKey, merged);
      contatosAlterados.push(merged);
    });

    const listaOrdenada = ordenarContatos(Array.from(mapa.values()));
    return { contatos: listaOrdenada, contatosAlterados };
  }, [getContatoTimestamp, normalizarContato, ordenarContatos]);

  const fetchUltimaAlteracaoContato = useCallback(async () => {
    try {
      const result = await fetchData('/WhatsAppWeb/GetUltimaAlteracaoContato');
      return parseDataAlteracao(result);
    } catch (error) {
      console.error('Erro ao buscar ultima alteracao de contatos:', error);
      return null;
    }
  }, [parseDataAlteracao]);

  const fetchMensagensByContato = useCallback(async (contatoId, { silent = false } = {}) => {
    if (!contatoId) {
      setMensagensContato([]);
      return [];
    }

    const contatoIdSolicitado = String(contatoId);
    const contatoAindaSelecionado = () => {
      const contatoAtual = contatoSelecionadoRef.current;
      const contatoAtualId = contatoAtual?.contatoIdApi ?? getContatoId(contatoAtual);
      return String(contatoAtualId ?? '') === contatoIdSolicitado;
    };

    if (!silent) {
      setLoadingMensagens(true);
    }
    try {
      const result = await fetchData('/WhatsAppWeb/GetMensagensByContato', { contatoId });
      const lista = Array.isArray(result) ? result : [];
      if (contatoAindaSelecionado()) {
        setMensagensContato(lista);
      }
      return lista;
    } catch (error) {
      console.error('Erro ao buscar mensagens por contato:', error);
      if (!silent && contatoAindaSelecionado()) {
        setMensagensContato([]);
        showAlert('Erro ao buscar mensagens do contato!', 'danger');
      }
      throw error;
    } finally {
      if (!silent) {
        setLoadingMensagens(false);
      }
    }
  }, [showAlert]);

  const enviarMensagemTexto = useCallback(async (contatoId, texto) => {
    const textoNormalizado = String(texto ?? '').trim();
    if (!contatoId || !textoNormalizado) {
      throw new Error('Contato e mensagem são obrigatórios para o envio.');
    }

    const result = await enviarMensagemTextoService(contatoId, textoNormalizado);
    const contatoAtual = contatoSelecionadoRef.current;
    const contatoAtualId = contatoAtual?.contatoIdApi ?? getContatoId(contatoAtual);

    if (String(contatoAtualId ?? '') === String(contatoId)) {
      const mensagemRetornada =
        result?.mensagem ??
        result?.message ??
        (
          result &&
          typeof result === 'object' &&
          (result.messageText || result.idMessage)
            ? result
            : null
        );
      const mensagemEnviada = {
        ...(mensagemRetornada && typeof mensagemRetornada === 'object' ? mensagemRetornada : {}),
        id: mensagemRetornada?.id ?? `local-${contatoId}-${Date.now()}`,
        messageText: mensagemRetornada?.messageText ?? mensagemRetornada?.texto ?? textoNormalizado,
        dataHora: mensagemRetornada?.dataHora ?? new Date().toISOString(),
        tipo: mensagemRetornada?.tipo ?? 1,
        fromMe: false,
      };

      setMensagensContato((prev) => [...prev, mensagemEnviada]);

      const atualizarResumoContato = (contato) => ({
        ...contato,
        mensagem: textoNormalizado,
        ultimaMensagem: textoNormalizado,
        dataHora: mensagemEnviada.dataHora,
        ultimaDataHora: mensagemEnviada.dataHora,
        hasUnread: false,
      });

      setContatos((prev) => ordenarContatos(prev.map((contato) => (
        getContatoKey(contato) === getContatoKey(contatoAtual)
          ? atualizarResumoContato(contato)
          : contato
      ))));

      setContatoSelecionado((prev) => {
        if (!prev || getContatoKey(prev) !== getContatoKey(contatoAtual)) return prev;
        const contatoAtualizado = atualizarResumoContato(prev);
        contatoSelecionadoRef.current = contatoAtualizado;
        return contatoAtualizado;
      });
    }

    return result;
  }, [ordenarContatos]);

  const enviarUrlApp = useCallback(async (contatoId) => {
    const result = await getUrlApp();
    const urlApp = result?.urlApp ?? result?.UrlApp;

    if (!urlApp || typeof urlApp !== 'string') {
      throw new Error('A URL do aplicativo não foi retornada.');
    }

    const texto = `🛍️ Para visualizar o cardápio ou fazer um pedido, acesse o link:\n ${urlApp.trim()}`;
    await enviarMensagemTexto(contatoId, texto);
    return urlApp.trim();
  }, [enviarMensagemTexto]);

  const atualizarContatoLocal = useCallback((contatoAtualizado) => {
    if (!contatoAtualizado) return;

    const contatoNormalizado = normalizarContato(contatoAtualizado, contatoAtualizado?.id ?? contatoSelecionadoRef.current?.id);
    const contatoKey = getContatoKey(contatoNormalizado);

    if (!contatoKey) return;

    setContatos((prev) => ordenarContatos(prev.map((contato) => (
      getContatoKey(contato) === contatoKey
        ? { ...contato, ...contatoNormalizado }
        : contato
    ))));

    setContatoSelecionado((prev) => {
      if (!prev || getContatoKey(prev) !== contatoKey) return prev;
      return { ...prev, ...contatoNormalizado };
    });
  }, [normalizarContato, ordenarContatos]);

  const marcarContatoComoLido = useCallback((contato) => {
    const contatoKey = getContatoKey(contato);
    if (!contatoKey) return;

    setContatos((prev) => prev.map((item) => (
      getContatoKey(item) === contatoKey
        ? { ...item, hasUnread: false }
        : item
    )));

    setContatoSelecionado((prev) => {
      if (!prev || getContatoKey(prev) !== contatoKey) return prev;
      return { ...prev, hasUnread: false };
    });
  }, []);

  const selecionarContato = useCallback(async (contato) => {
    const contatoNormalizado = contato ? { ...contato, hasUnread: false } : null;

    contatoSelecionadoRef.current = contatoNormalizado;
    setContatoSelecionado(contatoNormalizado);
    setMensagensContato([]);
    if (contatoNormalizado) {
      marcarContatoComoLido(contatoNormalizado);
    }

    const contatoId = contatoNormalizado?.contatoIdApi ?? getContatoId(contatoNormalizado);
    if (contatoId && String(contatoId).indexOf('tmp-') !== 0) {
      await fetchMensagensByContato(contatoId);
    }
  }, [fetchMensagensByContato, marcarContatoComoLido]);

  const fetchContatos = useCallback(async ({ autoSelectFirst = true, silent = false } = {}) => {
    setLoadingContatos(true);
    try {
      const result = await fetchData('/WhatsAppWeb/GetContatos');
      const lista = ordenarContatos(
        (Array.isArray(result) ? result : []).map((contato, index) => normalizarContato(contato, `tmp-${getContatoId(contato) ?? index}`))
      );

      setContatos(lista);

      if (!lista.length) {
        setContatoSelecionado(null);
        setMensagensContato([]);
        return [];
      }

      if (autoSelectFirst) {
        await selecionarContato(lista[0]);
      }

      return lista;
    } catch (error) {
      console.error('Erro ao buscar contatos do WhatsApp Web:', error);
      setContatos([]);
      setContatoSelecionado(null);
      setMensagensContato([]);
      if (!silent) {
        showAlert('Erro ao buscar contatos do WhatsApp!', 'danger');
      }
      throw error;
    } finally {
      setLoadingContatos(false);
    }
  }, [normalizarContato, ordenarContatos, selecionarContato, showAlert]);

  const fetchUltimos10Contatos = useCallback(async () => {
    const result = await fetchData('/WhatsAppWeb/GetUltimos10Contatos');
    return (Array.isArray(result) ? result : []).map((contato, index) => (
      normalizarContato(contato, `tmp-${getContatoId(contato) ?? index}`)
    ));
  }, [normalizarContato]);

  const processarContatoRecebido = useCallback(async (contatoRecebido, { recarregarMensagens = true } = {}) => {
    if (!contatoRecebido) return;

    const contatoNormalizado = normalizarContato(
      contatoRecebido,
      contatoRecebido?.id ?? `tmp-${getContatoId(contatoRecebido) ?? 'contato'}`
    );
    const contatoKey = getContatoKey(contatoNormalizado);
    if (!contatoKey) return;

    const dataEvento = parseDataAlteracao(
      contatoNormalizado?.ultimaDataHora ??
      contatoNormalizado?.UltimaDataHora ??
      contatoNormalizado?.dataHora ??
      contatoNormalizado?.DataHora ??
      contatoNormalizado?.dataAlteracao
    );

    if (dataEvento) {
      atualizarUltimaSincronizacao(dataEvento);
    }

    let contatoMesclado = null;

    setContatos((prev) => {
      const resultado = mergeContatos(prev, [contatoNormalizado]);
      contatoMesclado = resultado.contatos.find((item) => getContatoKey(item) === contatoKey) ?? null;
      return resultado.contatos;
    });

    const contatoAtual = contatoSelecionadoRef.current;
    if (!contatoAtual || getContatoKey(contatoAtual) !== contatoKey) return;

    const contatoSelecionadoMesclado = {
      ...contatoAtual,
      ...(contatoMesclado || contatoNormalizado),
      hasUnread: false,
    };

    setContatoSelecionado(contatoSelecionadoMesclado);
    marcarContatoComoLido(contatoSelecionadoMesclado);

    if (!recarregarMensagens) return;

    const contatoAtualId = contatoSelecionadoMesclado?.contatoIdApi ?? getContatoId(contatoSelecionadoMesclado);
    if (contatoAtualId && String(contatoAtualId).indexOf('tmp-') !== 0) {
      await fetchMensagensByContato(contatoAtualId, { silent: true });
      return;
    }
  }, [
    atualizarUltimaSincronizacao,
    fetchMensagensByContato,
    marcarContatoComoLido,
    mergeContatos,
    normalizarContato,
    parseDataAlteracao,
  ]);

  const sincronizarSeNecessario = useCallback(async () => {
    if (sincronizandoRef.current) return;

    sincronizandoRef.current = true;
    try {
      const dataUltimaAlteracao = await fetchUltimaAlteracaoContato();
      if (!dataUltimaAlteracao) return;

      const dataUltimaSincronizacao = ultimaSincronizacaoRef.current;
      if (!dataUltimaSincronizacao) {
        atualizarUltimaSincronizacao(dataUltimaAlteracao);
        return;
      }

      if (dataUltimaAlteracao.getTime() <= dataUltimaSincronizacao.getTime()) return;

      atualizarUltimaSincronizacao(dataUltimaAlteracao);
      const recentes = await fetchUltimos10Contatos();
      let contatosAlterados = [];

      setContatos((prev) => {
        const resultado = mergeContatos(prev, recentes);
        contatosAlterados = resultado.contatosAlterados;
        return resultado.contatos;
      });

      const contatoAtual = contatoSelecionadoRef.current;
      const contatoAtualId = contatoAtual?.contatoIdApi ?? getContatoId(contatoAtual);
      const contatoAtualKey = contatoAtual ? getContatoKey(contatoAtual) : '';

      if (!contatoAtualId) {
        return;
      }

      const contatoAtualizado = contatosAlterados.find((item) => getContatoKey(item) === contatoAtualKey);
      if (!contatoAtualizado) return;

      const contatoSelecionadoMesclado = {
        ...contatoAtual,
        ...contatoAtualizado,
        hasUnread: false,
      };

      setContatoSelecionado(contatoSelecionadoMesclado);
      marcarContatoComoLido(contatoSelecionadoMesclado);
      await fetchMensagensByContato(contatoAtualId, { silent: true });
    } catch {
      // polling silencioso
    } finally {
      sincronizandoRef.current = false;
    }
  }, [atualizarUltimaSincronizacao, fetchMensagensByContato, fetchUltimaAlteracaoContato, fetchUltimos10Contatos, marcarContatoComoLido, mergeContatos]);

  useEffect(() => {
    contatoSelecionadoRef.current = contatoSelecionado;
  }, [contatoSelecionado]);

  useEffect(() => {
    const inicializar = async () => {
      await fetchContatos();
      const dataUltimaAlteracao = await fetchUltimaAlteracaoContato();
      atualizarUltimaSincronizacao(dataUltimaAlteracao);
    };

    inicializar().catch(() => {});
  }, [atualizarUltimaSincronizacao, fetchContatos, fetchUltimaAlteracaoContato]);

  useWhatsappWebSSE({
    extrairContatoDoEvento,
    processarContatoRecebido,
    sincronizarSeNecessario,
  });

  const value = useMemo(() => ({
    contatos,
    contatoSelecionado,
    mensagensContato,
    ultimaSincronizacao,
    loadingContatos,
    loadingMensagens,
    checkWhatsNumber,
    enviarMensagemTexto,
    enviarUrlApp,
    fetchContatos,
    fetchMensagensByContato,
    marcarContatoComoLido,
    selecionarContato,
    atualizarContatoLocal,
  }), [
    contatos,
    contatoSelecionado,
    mensagensContato,
    ultimaSincronizacao,
    loadingContatos,
    loadingMensagens,
    checkWhatsNumber,
    enviarMensagemTexto,
    enviarUrlApp,
    fetchContatos,
    fetchMensagensByContato,
    marcarContatoComoLido,
    selecionarContato,
    atualizarContatoLocal,
  ]);

  return (
    <WhatsAppWebContext.Provider value={value}>
      {children}
    </WhatsAppWebContext.Provider>
  );
};
